import { isValid, startOfDay } from 'date-fns';

import { cn } from '@utils/cn';
import { fromStoryblokDateTime, isDateWithinRange } from '@utils/dates';
import type { AlertStoryblok } from '@utils/storyblok/storyblok';
import { RichText } from '@common/typography/RichText';

type AlertProps = Omit<AlertStoryblok, '_uid' | 'component'> & {
  className?: string;
  selectedDates?: Date[];
  severity?: 'error' | 'warning' | 'info' | 'success';
  variant?: 'filled' | 'outlined' | 'standard';
};

/**
 * Determines if the alert should be displayed by checking
 * whether the current date or any selected dates
 * fall within the [startDate, endDate] range.
 */
function shouldDisplayAlert(startDate?: string, endDate?: string, selectedDates?: Date[]) {
  const start = startDate ? startOfDay(fromStoryblokDateTime(startDate)) : null;
  const end = endDate ? startOfDay(fromStoryblokDateTime(endDate)) : null;

  // If no dates are selected, compare "today's" date to [start, end].
  if (!selectedDates || selectedDates.length <= 0) {
    return isDateWithinRange(new Date(), start, end);
  }

  // Check each of the selected dates; if invalid, fall back to "today".
  return selectedDates.some((selectedDate) => {
    const validDate = isValid(selectedDate) ? selectedDate : new Date();
    return isDateWithinRange(validDate, start, end);
  });
}

export function Alert({
  endDate,
  selectedDates,
  severity = 'info',
  startDate,
  text,
  variant = 'standard',
  className,
}: AlertProps) {
  if (!shouldDisplayAlert(startDate, endDate, selectedDates)) {
    return null;
  }

  // Opinion: define severity-based styles here
  // Reference: https://tailwindcss.com/docs (general docs on utility classes)
  const severityClasses: Record<string, string> = {
    error: 'border-red-500 bg-red-50 text-red-800',
    warning: 'border-yellow-500 bg-yellow-50 text-yellow-800',
    info: 'border-blue-500 bg-blue-50 text-blue-800',
    success: 'border-green-500 bg-green-50 text-green-800',
  };

  // Opinion: variant styling logic could be expanded, e.g. outlined or filled
  // For simplicity, we only show how you might handle a 'filled' variant
  const variantClasses: Record<string, string> = {
    filled: 'shadow-inner', // example extra styling for 'filled'
    outlined: 'border border-current',
    standard: '',
  };

  return (
    <div
      className={cn(
        'p-4 border-l-4 rounded-md',
        severityClasses[severity] ?? severityClasses.info,
        variantClasses[variant] ?? variantClasses.standard,
        className,
      )}
      role="alert"
    >
      <RichText text={text} />
    </div>
  );
}
